/**
 * Spring Theme Bloom Store
 *
 * Tracks bloom progress as a 0–1 value that smoothly interpolates between
 * three landscape variants (few → some → all). The progress maps to
 * per-variant opacity values for crossfading.
 *
 * Progress mapping:
 *   0.0 = 100% "few"
 *   0.5 = 100% "some"
 *   1.0 = 100% "all"
 *   Values in between crossfade smoothly.
 *
 * Persisted to localStorage so progress survives page reloads.
 */

const STORAGE_KEY = 'bonusly:seasonal-theme:spring-2026:bloom-progress';

const EVENT_NAME = 'spring-bloom-change';

let bloomProgress = 0;

const load = () => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		bloomProgress = stored ? Math.max(0, Math.min(1, parseFloat(stored) || 0)) : 0;
	} catch {
		bloomProgress = 0;
	}
};

const save = () => {
	try {
		localStorage.setItem(STORAGE_KEY, String(bloomProgress));
	} catch {
		// localStorage unavailable
	}
};

const dispatch = () => window.dispatchEvent(new CustomEvent(EVENT_NAME));

load();

// TODO: Wire up to recognition activity. Until then, always return 0
// so only the "few" landscape variant is shown for all users.
export const getBloomProgress = (): number => 0;

/**
 * Returns opacity values (0–1) for each landscape variant based on current progress.
 * Crossfades between adjacent stages so the transition is smooth.
 */
export const getBloomOpacities = (progress = bloomProgress): { few: number; some: number; all: number } => {
	if (progress <= 0) return { few: 1, some: 0, all: 0 };
	if (progress >= 1) return { few: 0, some: 0, all: 1 };

	if (progress <= 0.5) {
		// Crossfade few → some
		const t = progress / 0.5;
		return { few: 1 - t, some: t, all: 0 };
	}

	// Crossfade some → all
	const t = (progress - 0.5) / 0.5;
	return { few: 0, some: 1 - t, all: t };
};

export const setBloomProgress = (p: number) => {
	bloomProgress = Math.max(0, Math.min(1, p));
	save();
	dispatch();
};

export const incrementBloom = (amount = 0.05) => {
	setBloomProgress(bloomProgress + amount);
};

export const resetBloom = () => {
	setBloomProgress(0);
};

export const onBloomChange = (callback: () => void) => {
	window.addEventListener(EVENT_NAME, callback);
	return () => window.removeEventListener(EVENT_NAME, callback);
};
